// @ts-check
import { gql } from '@apollo/client/core';
import { View } from '@react-pdf/renderer';
import { Text } from '../../../pdf-design-system';
import { useProduct, useTheme } from './UrbanihofInfoSheet';
import React from 'react';
import { useLingui } from '@lingui/react';
import LogoQRCode from '../../VieVinum/components/LogoQRCode';
import OrganicEUIcon from '../OrganicEUIcon';
import { t, Trans } from '@lingui/macro';

export default function UrbanihofInfoSheetFooter({ style, ...rest }) {
  return (
    <View
      style={[{ flexDirection: 'row', alignItems: 'center' }, style]}
      {...rest}
    >
      <FooterBox style={{ flex: 1 }} />
      <QRCode style={{}} />
    </View>
  );
}

function FooterBox({ style, ...rest }) {
  const { product } = useAfterQuery();
  const { i18n } = useLingui();
  return (
    <View
      style={[
        {
          borderTopWidth: 0.5,
          borderBottomWidth: 0.5,
          paddingVertical: '2mm',
          flexDirection: 'row',
        },
        style,
      ]}
      {...rest}
    >
      <Value title={i18n._(t`Contains`)}>
        {product.containsSulfites === false
          ? i18n._(t`No sulfites`)
          : i18n._(t`Sulfites`)}
      </Value>
      <Value title={i18n._(t`GTIN bottle`)}>
        {product.bottle?.GTIN || '–'}
      </Value>
      <Value
        title={i18n._(
          t`GTIN ${i18n._(product.case?.caseMaterial) || ''} | ${
            product.case?.bottlesPerCase || 6
          } bottles`
        )}
        showSeparator={product.isCertifiedOrganic}
      >
        {product.case?.GTIN || '–'}
      </Value>
      <OrganicCertification style={{}} />
    </View>
  );
}

const site = 'https://vievinum-2022.netlify.app';

function QRCode({ style, ...rest }) {
  const { wine } = useAfterQuery();
  // console.log(wine.unserweinCode);
  return (
    <View style={{ paddingLeft: '2mm' }} {...rest}>
      <LogoQRCode
        url={`${site}/${wine.productId}`}
        style={[{ width: '16mm', height: '16mm' }, style]}
        margin={0}
        // logo="unserwein"
        color="#000"
        // color={theme.colors.primary}
        {...rest}
      />
    </View>
  );
}

function Value({
  title,
  children,
  isEmpty = children == null,
  showSeparator = true,
}) {
  if (isEmpty) return null;
  return (
    <>
      <View>
        <Text variant="bold" style={{ lineHeight: 1.1 }}>
          {title}
        </Text>
        <Text style={{ lineHeight: 1.1 }}>{children}</Text>
      </View>
      <ValueSeparator showBorder={showSeparator} />
    </>
  );
}
function ValueSeparator({ showBorder = true }) {
  return (
    <View style={{ flex: 1, alignItems: 'stretch', flexDirection: 'row' }}>
      {showBorder && (
        <View style={{ borderRightWidth: 0.5, marginHorizontal: 'auto' }} />
      )}
    </View>
  );
}

function OrganicCertification({ style, ...rest }) {
  const { product } = useAfterQuery();
  if (!product.isCertifiedOrganic) return null;
  return (
    <>
      <View style={[{ paddingHorizontal: '2mm' }, style]} {...rest}>
        <Text style={{ lineHeight: 1.1 }}>
          <Trans>AT-BIO-402</Trans>
        </Text>
        <Text style={{ lineHeight: 1.1 }}>
          <Trans>AT Agriculture</Trans>
        </Text>
      </View>
      {/* <View style={{ borderWidth: 0.5, width: '15mm' }} /> */}
      <OrganicEUIcon
        color="#000"
        stroke="#000"
        backgroundColor="#fff"
        style={{ width: '15mm', height: '10mm' }}
      />
    </>
  );
}

UrbanihofInfoSheetFooter.fragment = gql`
  fragment UrbanihofInfoSheetFooter on Product {
    __typename
    productId
    containsSulfites
    isCertifiedOrganic
    bottleVariants {
      GTIN
      bottleSize
      internalId
    }
    packagingVariants {
      GTIN
      bottlesPerCase
      caseMaterial
    }
  }
`;

function useAfterQuery() {
  /** @type {import('./@types/UrbanihofInfoSheetFooter').UrbanihofInfoSheetFooterFragment} product */
  const rawProduct = useProduct();
  const rawWine = rawProduct.__typename === 'Wine' ? rawProduct : null;
  const bottle = rawProduct.bottleVariants?.find(({ GTIN }) => GTIN);
  const box = rawProduct.packagingVariants?.find(({ GTIN }) => GTIN);
  const unserweinCode = rawProduct.bottleVariants.find(({ internalId }) =>
    internalId?.startsWith('n')
  );
  const product = {
    ...rawProduct,
    bottle,
    case: box,
  };
  const wine = rawWine && {
    ...rawWine,
    unserweinCode: unserweinCode?.internalId,
  };
  return { product, wine };
}
